'use client';

import Image from 'next/image';
import { useMemo } from 'react';
import type { PickemDto } from '@/api/backoffice-zod-schemas';
import { Button } from '@/components/button';
import { GameIcon } from '@/components/GameIcon';
import { TeamPlaceholderIcon } from '@/components/icons/teams/TeamPlaceholderIcon';
import type { Game } from '@/constants/game.const';
import { formatMatchTime } from '@/utils/matches';

interface TeamProps {
  name: string;
  logo?: string;
}

const TeamCard = ({ name, logo }: TeamProps) => {
  return (
    <div className="flex w-full items-center justify-center gap-2 rounded-lg border border-white-o15 bg-component-bg-o99 p-2 backdrop-blur-sm sm:min-w-0 sm:flex-1 sm:gap-3 sm:p-3 lg:w-[180px] lg:p-4 xl:w-[200px]">
      <div className="relative h-6 w-6 flex-shrink-0 sm:h-7 sm:w-7 lg:h-8 lg:w-8">
        {logo ? (
          <Image
            alt={`${name} logo`}
            className="object-contain"
            fill
            sizes="(min-width: 1024px) 32px, (min-width: 640px) 28px, 24px"
            src={logo}
          />
        ) : (
          <TeamPlaceholderIcon />
        )}
      </div>
      <span className="truncate font-medium text-white text-xs sm:text-sm lg:text-sm">
        {name}
      </span>
    </div>
  );
};

const VSIndicator = () => {
  return (
    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-light-gray sm:h-7 sm:w-7 lg:h-8 lg:w-8">
      <span className="font-bold text-white text-xs lg:text-xs">VS</span>
    </div>
  );
};

export type PickEmsCardProps = {
  pickem: PickemDto;
  game: Game | null;
  targetLink: string;
  ctaText: string;
};

export const PickEmsCard = ({
  pickem,
  game,
  targetLink,
  ctaText,
}: PickEmsCardProps) => {
  const formattedTime = useMemo(() => {
    return formatMatchTime(pickem?.match?.beginAt);
  }, [pickem.match?.beginAt]);

  // Extract team data
  const teams = pickem.match?.teamMatches || [];
  const team1 = teams[0];
  const team2 = teams[1];

  // Fallback team data if no real data is available
  const teamA = {
    name: team1?.team?.name || 'TBD',
    logo: team1?.team?.imageUrl ?? undefined,
  };

  const teamB = {
    name: team2?.team?.name || 'TBD',
    logo: team2?.team?.imageUrl ?? undefined,
  };

  // Extract tournament info
  const tournamentType = `BO${pickem.match?.numberOfGames || '?'}`;
  const tournamentName = pickem.match?.league?.name || 'Unknown Tournament';

  // Show fallback message if no match data
  if (!(pickem.match && teams.length)) {
    return (
      <div className="w-full rounded-lg border border-white-o15 bg-dark-gray/40 p-4 backdrop-blur-sm sm:rounded-xl sm:p-6">
        <div className="py-4 text-center sm:py-6 lg:py-8">
          <p className="mb-3 text-gray-400 text-sm sm:mb-4 sm:text-base">
            No match data available
          </p>
          <Button
            as="link"
            className="w-full text-sm sm:text-base"
            href={targetLink}
            size="lg"
            variant="outlineWithBg"
          >
            Browse Pickems
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-lg border-1 border-white-o15 bg-dark-gray/40 p-4 backdrop-blur-sm sm:rounded-xl sm:p-5 lg:p-6">
      {/* Header with time and tournament */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-y-2 sm:mb-5 lg:mb-6">
        <div className="flex items-center gap-1 sm:gap-2">
          {game && (
            <GameIcon
              className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7"
              game={game}
            />
          )}
          <span className="font-medium text-white text-xs sm:text-sm lg:text-sm">
            {formattedTime}
          </span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          {/* League logo */}
          {pickem.match.league?.imageUrl && (
            <div className="relative h-6 w-6 flex-shrink-0 sm:h-7 sm:w-7 lg:h-8 lg:w-8">
              <Image
                alt={pickem.match.league.name ?? 'League logo'}
                className="object-contain"
                fill
                sizes="(min-width: 1024px) 32px, (min-width: 640px) 28px, 24px"
                src={pickem.match.league.imageUrl}
              />
            </div>
          )}
          <div className="text-left">
            <div className="font-medium text-white text-xs sm:text-sm lg:text-sm">
              {tournamentType}
            </div>
            <div className="truncate text-blue-gray text-xs lg:text-xs">
              {tournamentName}
            </div>
          </div>
        </div>
      </div>

      {/* Teams matchup */}
      <div className="mb-4 flex flex-col items-center justify-between gap-2 sm:mb-5 sm:flex-row sm:gap-3 md:gap-4 lg:mb-6">
        <TeamCard logo={teamA.logo} name={teamA.name} />
        <VSIndicator />
        <TeamCard logo={teamB.logo} name={teamB.name} />
      </div>

      <div className="flex justify-center">
        <Button
          as="link"
          className="w-full text-sm text-white sm:text-base md:w-80"
          href={targetLink}
          size="lg"
          variant="primary"
        >
          {ctaText}
        </Button>
      </div>
    </div>
  );
};
